/**
 * File Retention Utilities
 * Helper functions for file retention and backup period management
 */

import SubscriptionService from '../services/SubscriptionService';

/**
 * Calculate backup expiry date for a file based on upload date and plan
 * @param {Date|Timestamp} uploadDate - When the file was uploaded
 * @param {string} planId - Subscription plan ID
 * @returns {Date|null} Expiry date (null for forever backup)
 */
export const calculateBackupExpiryDate = (uploadDate, planId) => {
  return SubscriptionService.calculateBackupExpiryDate(uploadDate, planId);
};

/**
 * Check if a file is within backup period
 * @param {Date|Timestamp} uploadDate - When the file was uploaded
 * @param {string} planId - Subscription plan ID
 * @returns {boolean} True if file is within backup period
 */
export const isFileWithinBackupPeriod = (uploadDate, planId) => {
  return SubscriptionService.checkFileRetentionLimits(uploadDate, planId);
};

/**
 * Get backup period for a subscription plan
 * @param {string} planId - Plan identifier
 * @returns {number} Backup period in days (-1 for forever)
 */
export const getBackupPeriod = (planId) => {
  return SubscriptionService.getBackupPeriod(planId);
};

/**
 * Create file backup info object for an order
 * @param {string} planId - Subscription plan ID
 * @param {Date|Timestamp} uploadDate - When files were uploaded
 * @param {Object} fileCounts - Count of files by type
 * @returns {Object} File backup info object
 */
export const createFileBackupInfo = (planId, uploadDate, fileCounts = {}) => {
  const backupDays = getBackupPeriod(planId);
  const backupExpiryDate = calculateBackupExpiryDate(uploadDate, planId);
  const backupEligible = isFileWithinBackupPeriod(uploadDate, planId);

  const totalFiles = Object.values(fileCounts).reduce((sum, count) => sum + (count || 0), 0);

  return {
    totalFiles,
    backupExpiryDate: backupExpiryDate ? backupExpiryDate : null,
    fileTypes: {
      orderDetails: { count: fileCounts.orderDetails || 0 },
      bills: { count: fileCounts.bills || 0 },
      challans: { count: fileCounts.challans || 0 },
      bilty: { count: fileCounts.bilty || 0 }, // FIX: Use 'bilty' (singular) to match database structure
      products: { count: fileCounts.products || 0 }
    },
    backupEligible
  };
};

/**
 * Map fileType from upload format to fileBackupInfo format
 * @param {string} fileType - File type from upload ('order', 'bill', etc.)
 * @returns {string} File type for fileBackupInfo ('orderDetails', 'bills', etc.)
 */
const mapFileTypeToBackupInfoKey = (fileType) => {
  const mapping = {
    'order': 'orderDetails',
    'bill': 'bills',
    'challan': 'challans',
    'bilty': 'bilty',
    'product': 'products'
  };
  return mapping[fileType] || fileType; // Return mapped value or original if not found
};

/**
 * Update file backup info when files are added or removed
 * @param {Object} existingBackupInfo - Current backup info
 * @param {string} fileType - Type of file being added/removed (from upload: 'order', 'bill', etc.)
 * @param {number} countChange - Number of files added (positive) or removed (negative)
 * @param {string} planId - Subscription plan ID
 * @param {Date|Timestamp} uploadDate - When files were uploaded
 * @returns {Object} Updated backup info
 */
export const updateFileBackupInfo = (existingBackupInfo, fileType, countChange, planId, uploadDate) => {
  // FIX: Map fileType from upload format to fileBackupInfo format
  const backupInfoKey = mapFileTypeToBackupInfoKey(fileType);
  
  const currentTotal = existingBackupInfo?.totalFiles || 0;
  const currentFileTypes = existingBackupInfo?.fileTypes || {};
  const currentTypeCount = currentFileTypes[backupInfoKey]?.count || 0;

  const newTotal = Math.max(0, currentTotal + countChange);
  const newTypeCount = Math.max(0, currentTypeCount + countChange);

  const backupDays = getBackupPeriod(planId);
  const backupExpiryDate = calculateBackupExpiryDate(uploadDate, planId);
  const backupEligible = isFileWithinBackupPeriod(uploadDate, planId);

  return {
    totalFiles: newTotal,
    backupExpiryDate: backupExpiryDate ? backupExpiryDate : null,
    fileTypes: {
      ...currentFileTypes,
      [backupInfoKey]: { count: newTypeCount } // FIX: Use mapped key instead of original fileType
    },
    backupEligible
  };
};

/**
 * Format backup period for display
 * @param {number} backupDays - Backup period in days
 * @returns {string} Formatted backup period
 */
export const formatBackupPeriod = (backupDays) => {
  if (backupDays === -1) {
    return 'Forever';
  }
  if (backupDays === 1) {
    return '1 day';
  }
  return `${backupDays} days`;
};

/**
 * Get days remaining until backup expiry
 * @param {Date|Timestamp} backupExpiryDate - Backup expiry date
 * @returns {number} Days remaining (negative if expired)
 */
export const getDaysUntilExpiry = (backupExpiryDate) => {
  if (!backupExpiryDate) {
    return Infinity; // Forever backup
  }

  const expiryDate = backupExpiryDate?.toDate ? backupExpiryDate.toDate() : new Date(backupExpiryDate);
  const now = new Date();
  
  // Set both dates to start of day for consistent calculation
  const expiryStartOfDay = new Date(expiryDate.getFullYear(), expiryDate.getMonth(), expiryDate.getDate());
  const nowStartOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  const diffTime = expiryStartOfDay - nowStartOfDay;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  // Ensure we never return 0 for files that should have time remaining
  // If the calculation results in 0 or negative, it means the file is expired
  return Math.max(0, diffDays);
};

/**
 * Check if files are approaching expiry (within 7 days)
 * @param {Date|Timestamp} backupExpiryDate - Backup expiry date
 * @returns {boolean} True if files are approaching expiry
 */
export const isApproachingExpiry = (backupExpiryDate) => {
  const daysRemaining = getDaysUntilExpiry(backupExpiryDate);
  return daysRemaining <= 7 && daysRemaining >= 0;
};

/**
 * Get backup status message for UI
 * @param {Object} fileBackupInfo - File backup info object
 * @param {string} planId - Subscription plan ID
 * @returns {Object} Status message and type
 */
export const getBackupStatusMessage = (fileBackupInfo, planId) => {
  if (!fileBackupInfo || fileBackupInfo.totalFiles === 0) {
    return {
      type: 'info',
      message: 'No files to backup',
      action: null
    };
  }

  const backupDays = getBackupPeriod(planId);
  
  if (backupDays === -1) {
    return {
      type: 'success',
      message: 'Files are backed up forever',
      action: null
    };
  }

  if (!fileBackupInfo.backupEligible) {
    return {
      type: 'warning',
      message: `${fileBackupInfo.totalFiles} files have exceeded the ${backupDays}-day backup period`,
      action: 'Files will be cleaned up automatically'
    };
  }

  const daysRemaining = getDaysUntilExpiry(fileBackupInfo.backupExpiryDate);
  
  if (daysRemaining === 0) {
    return {
      type: 'expired',
      message: `${fileBackupInfo.totalFiles} files have expired and will be cleaned up`,
      action: 'Files will be automatically deleted'
    };
  } else if (daysRemaining <= 7) {
    return {
      type: 'warning',
      message: `${fileBackupInfo.totalFiles} files will expire in ${daysRemaining} days`,
      action: 'Consider upgrading to extend backup period'
    };
  }

  return {
    type: 'info',
    message: `${fileBackupInfo.totalFiles} files backed up for ${backupDays} days`,
    action: null
  };
};
